import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // 비밀번호 입력 후 자동으로 캡차 표시
  useEffect(() => {
    if (password.length > 0 && !showCaptcha) {
      setShowCaptcha(true);
    }
    if (email.length > 0 && !showCaptcha) {
      setShowCaptcha(true);
    }
  }, [password, email]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!captchaVerified) {
      setError('Please verify the captcha');
      return;
    }

    setLoading(true);
    setError('');

    const success = await login(email, password);
    
    if (success) {
      navigate('/');
    } else {
      setError('Invalid username or password');
      setLoading(false);
      setCaptchaVerified(false);
      setShowCaptcha(false);
    }
  };

  const handleCaptchaVerify = () => {
    setCaptchaVerified(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0D0E11] via-[#111319] to-[#16181D]">
      {/* Header */}
      <header className="bg-[#1a1c24] border-b border-[#FFD349]/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-slot-machine-line text-[#FFD349] text-2xl"></i>
              </div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-[#FFD349] to-[#FFC107] bg-clip-text text-transparent" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                DIGITWISE
              </h1>
            </Link>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/" className="text-white/70 hover:text-white transition-colors text-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Home
              </Link>
              <Link to="/signup" className="text-white/70 hover:text-white transition-colors text-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Sign Up
              </Link>
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-white/70 hover:text-white transition-colors cursor-pointer"
            >
              <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-2xl`}></i>
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 border-t border-[#FFD349]/20 pt-4">
              <nav className="flex flex-col gap-4">
                <Link to="/" className="text-white/70 hover:text-white transition-colors text-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Home
                </Link>
                <Link to="/signup" className="text-white/70 hover:text-white transition-colors text-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Sign Up
                </Link>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-md mx-auto px-4 py-12">
        <div className="bg-[#1a1c24] border border-[#FFD349]/20 rounded-xl p-8 shadow-[0px_0px_30px_rgba(255,211,73,0.2)]">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Orbitron, sans-serif' }}>
              Welcome Back
            </h2>
            <p className="text-white/60 text-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Login to continue your gaming journey
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-200 text-sm flex items-center gap-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              <i className="ri-error-warning-line text-xl"></i>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Username
              </label>
              <div className="relative">
                <input
                  type="name"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 pl-12 bg-[#0D0E11] border border-[#FFD349]/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FFD349] text-sm"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                  placeholder="Enter your username"
                  required
                />
                <i className="ri-user-line absolute left-4 top-1/2 -translate-y-1/2 text-white/40 text-lg"></i>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-white/80 mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 pl-12 pr-12 bg-[#0D0E11] border border-[#FFD349]/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FFD349] text-sm"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                  placeholder="Enter your password"
                  required
                />
                <i className="ri-lock-line absolute left-4 top-1/2 -translate-y-1/2 text-white/40 text-lg"></i>
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors cursor-pointer"
                >
                  <i className={`ri-eye-${showPassword ? 'off' : ''}-line text-lg`}></i>
                </button>
              </div>
            </div>

            {/* Captcha - 비밀번호 입력 후 자동 표시 */}
            {showCaptcha && (
              <div className="bg-[#0D0E11] border border-[#FFD349]/20 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-white/80 text-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    Verify you're human
                  </p>
                  {captchaVerified && (
                    <i className="ri-checkbox-circle-fill text-green-400 text-xl"></i>
                  )}
                </div>
                
                {!captchaVerified ? (
                  <div className="bg-[#1a1c24] border border-[#FFD349]/30 rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <input
                        type="checkbox"
                        id="captcha"
                        onChange={handleCaptchaVerify}
                        className="w-5 h-5 rounded border-[#FFD349]/30 bg-[#0D0E11] text-[#FFD349] focus:ring-2 focus:ring-[#FFD349] cursor-pointer"
                      />
                      <label htmlFor="captcha" className="text-white text-sm cursor-pointer" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        I'm not a robot
                      </label>
                    </div>
                    <div className="flex items-center gap-2 text-white/40 text-xs" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      <i className="ri-shield-check-line"></i>
                      <span>Protected by reCAPTCHA</span>
                    </div>
                  </div>
                ) : (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 flex items-center gap-2">
                    <i className="ri-checkbox-circle-fill text-green-400 text-xl"></i>
                    <span className="text-green-400 text-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      Verification successful
                    </span>
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-[#FFD349]/30 bg-[#0D0E11] text-[#FFD349] focus:ring-2 focus:ring-[#FFD349] cursor-pointer"
                />
                <span className="text-white/70" style={{ fontFamily: 'Montserrat, sans-serif' }}>Remember me</span>
              </label>
              <a href="#" className="text-[#FFD349] hover:text-[#FFC107] transition-colors" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Forgot password?
              </a>
            </div>

            <button
              type="submit"
              disabled={loading || !captchaVerified}
              className="w-full bg-gradient-to-r from-[#FFD349] to-[#FFC107] text-black font-bold py-4 rounded-lg hover:shadow-lg hover:shadow-[#FFD349]/30 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  Logging in...
                </span>
              ) : (
                'Login'
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-white/60 text-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Don't have an account?{' '}
              <Link to="/signup" className="text-[#FFD349] hover:text-[#FFC107] font-semibold transition-colors">
                Sign Up
              </Link>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
